//src/pages/EditAppointmentModal/index.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import {
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  LARGE_FORM_MODAL_SX,
  DENSE_FORM_BODY_SX,
} from "../../utils/formModalStyles";
import { updateAppointment, getAppointmentsByDoctorAndDate } from "../../services/appointmentService"; // Assume you have this service
import { getAllDepartment } from "../../services/departmentService"; // Service to fetch departments
import { getDoctorById, getDoctorByDepartmentId, getDoctorsByDepartmentIdMultiple } from "../../services/doctorService"; // Service to fetch doctors
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { format } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { rescheduleAppointmentReminders } from "../../services/appointmentReminderService";
import { getPatientById, syncPatientTypeForWorkflow } from "../../services/patientService";
import { rememberInvoiceDepartment } from "../../utils/invoiceDepartmentMemory";
import { filterDepartmentsForOpdWorkflow } from "../../utils/subscriptionScopeUtils";

const validationSchema = Yup.object({
  doctorId: Yup.number().required("Doctor is required"),
  appointment_date: Yup.date().required("Appointment Date is required"),
  time: Yup.string().required("Appointment Time is required"),
});

const EditAppointmentModal = ({ open, onClose, appointment, doctor, departmentId, departmentName, patient, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [formData, setFormData] = useState(appointment || {});
  const [existingAppointments, setExistingAppointments] = useState([]); // State for existing appointments
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  useEffect(() => {
    if (appointment) {
      // Use patientId from appointment (which should already be corrected by AppointmentsTable)
      // But ensure we have a valid patientId
      const patientId = appointment.patientId || "";
      const appointmentDateTime = appointment.appointmentDateTime ? dayjs(appointment.appointmentDateTime) : null;
      const doctorId = appointment.doctorId || doctor?.id || '';
      
      // Prioritize appointment.departmentId if available, otherwise use prop departmentId
      // Also get departmentId from doctor object if available
      const finalDepartmentId = appointment.departmentId || 
                                doctor?.departmentId || 
                                departmentId || 
                                '';
      const finalDepartmentName = appointment.departmentName || 
                                  doctor?.departmentName || 
                                  departmentName || 
                                  '';
      
      const updatedFormData = {
        ...appointment,
        patientId: patientId,
        departmentId: finalDepartmentId ? Number(finalDepartmentId) : '',
        departmentName: finalDepartmentName,
        doctorId: doctorId, // Set doctorId - it will be validated when doctors list loads
        appointment_date: appointmentDateTime,
        time: "", // Initialize time
        isCancel: appointment.isCancel || false,
      };
      
      setFormData(updatedFormData);
      
      // Also update formik values directly to ensure form updates immediately
      formik.setValues(updatedFormData);
      
      // Prefer the provided doctor object when available, but ensure it has visitingSchedules
      // Check that visitingSchedules is an object (not an array)
      const hasValidSchedules = doctor?.visitingSchedules && 
                                typeof doctor.visitingSchedules === 'object' && 
                                !Array.isArray(doctor.visitingSchedules);
      
      if (doctor && hasValidSchedules) {
        setSelectedDoctor(doctor);
      } else if (doctorId) {
        // Always fetch full doctor details (with visitingSchedules) when modal opens
        fetchDoctorName(doctorId).catch(() => {});
      }
      
      // Ensure doctors list is available for the preselected department
      // Use finalDepartmentId instead of prop departmentId
      if (finalDepartmentId) {
        fetchDoctors(Number(finalDepartmentId)).catch(() => {});
      }
      
      // Fetch existing appointments when appointment_date and doctorId are available
      if (appointmentDateTime && doctorId) {
        fetchExistingAppointments(doctorId, appointmentDateTime).catch(() => {});
      }
    }
  }, [appointment, departmentId, departmentName, doctor]);

  // console.log("doctor:-", doctor);
  // console.log("doctors:-", doctors);
  // console.log("selectedDoctor:-", selectedDoctor);
  
  const formik = useFormik({
    initialValues: formData,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {

      if (!formik.values.time) {
        toast.error("Please select a valid time.");
        return;
      }      

      // Check if the selected date is in the past
      if (dayjs(values.appointment_date).isBefore(dayjs(), "day")) {
        console.error("Selected date is invalid. Please choose a future date.");
        return;
      }
    
      if (!appointment?.id) {
        console.error("Appointment ID is missing. Cannot update appointment.");
        toast.error("Appointment ID is missing. Cannot update appointment.");
        return;
      }
    
      try {
        // Ensure time is parsed into 24-hour format before combining with the date
        const formattedTime = dayjs(values.time, ["h:mm A"]).format("HH:mm");
        const appointmentDateTime = `${dayjs(values.appointment_date).format("YYYY-MM-DD")}T${formattedTime}`;

        // Use patient ID from current context if available, otherwise use from form values
        // This ensures we use the correct organization-specific patient ID
        const correctPatientId = patient?.id || values.patientId || appointment.patientId;
        
        // Ensure patientId and doctorId are numbers
        const patientIdNum = correctPatientId ? Number(correctPatientId) : null;
        const doctorIdNum = values.doctorId ? Number(values.doctorId) : null;
        
        if (!patientIdNum) {
          toast.error("Patient ID is missing. Cannot update appointment.");
          return;
        }
        
        if (!doctorIdNum) {
          toast.error("Doctor ID is missing. Cannot update appointment.");
          return;
        }
        
        const updatedAppointment = {
          patientId: patientIdNum,
          doctorId: doctorIdNum,
          appointmentDateTime, // Correctly formatted ISO 8601 datetime
          // departmentId: values.departmentId, // Add departmentId
          isCancel: values.isCancel || false,
        };
    
        // Log the updated appointment data for debugging
        console.log("[EditAppointmentModal] Updating appointment:", {
          appointmentId: appointment.id,
          updatedData: updatedAppointment
        });
    
        const updatedAppt = await updateAppointment(appointment.id, updatedAppointment);
        rememberInvoiceDepartment(
          null,
          {
            patientId: patientIdNum,
            appointmentId: updatedAppt?.id || updatedAppt?.appointmentId || appointment.id,
          },
          {
            departmentId: values.departmentId,
            departmentName: values.departmentName,
            doctorId: doctorIdNum,
          }
        );
        await syncPatientTypeForWorkflow(patientIdNum, "OUT", { force: true });

        // Reschedule appointment reminders if appointment time/date changed (non-blocking)
        if (updatedAppt && (appointment.appointmentDateTime !== appointmentDateTime || appointment.doctorId !== values.doctorId)) {
          try {
            // Use correct patient ID for reminder rescheduling
            const correctPatientIdForReminder = patient?.id || values.patientId;
            
            // Fetch patient and doctor details for reminder
            const [patientDetails, doctorDetails] = await Promise.all([
              getPatientById(correctPatientIdForReminder).catch(() => null),
              getDoctorById(values.doctorId).catch(() => null),
            ]);

            await rescheduleAppointmentReminders({
              appointmentId: appointment.id,
              appointmentDateTime,
              patientId: correctPatientIdForReminder,
              doctorId: values.doctorId,
              isVirtual: appointment.isVirtual || appointment.is_virtual || false,
              virtualLink: appointment.virtualLink || appointment.virtual_link || appointment.virtualMeeting?.url || null,
              patientMobile: patientDetails?.mobile || null,
              patientName: patientDetails?.name || null,
              doctorMobile: doctorDetails?.mobile || null,
              doctorName: doctorDetails?.name || null,
              departmentName: doctorDetails?.departmentName || doctorDetails?.department_name || null,
            });
            console.log("Appointment reminders rescheduled successfully");
          } catch (error) {
            console.error("Failed to reschedule appointment reminders:", error);
            // Don't block the flow if reminder rescheduling fails
          }
        }

        setSuccessDialogOpen(true);

        } catch (error) {
        console.error("[EditAppointmentModal] Error updating appointment:", error);
        
        // Provide more helpful error message
        const errorMessage = error.message || error.response?.data?.message || "Failed to update appointment";
        
        if (errorMessage.includes("not found") || errorMessage.includes("Appointment not found")) {
          toast.error(`Appointment not found. The appointment may not exist in the organization-specific table. Please check if the appointment was created for the correct organization.`);
        } else {
          toast.error(`Failed to update appointment: ${errorMessage}`);
        }
      }
    },
  });

  const fetchExistingAppointments = async (doctorId, appointmentDate) => {
    try {
      // console.log("Fetching appointments for Doctor ID:", doctorId, "on Date:", appointmentDate.format("YYYY-MM-DD"));
      const appointments = await getAppointmentsByDoctorAndDate(doctorId, appointmentDate.format("YYYY-MM-DD"));
      console.log("existing appointment is :- ", appointments);
      setExistingAppointments(appointments);
    } catch (error) {
      console.error("Error fetching existing appointments:", error);
    }
  };

  useEffect(() => {
    if (appointment) {
      const appointmentDateTime = appointment.appointmentDateTime
        ? dayjs(appointment.appointmentDateTime)
        : null;
  
      const prefilledTime = appointmentDateTime
        ? appointmentDateTime.format("hh:mm A") // Extracts time in 12-hour format (e.g., "02:30 PM")
        : "";
  
      // Prioritize appointment.departmentId if available, otherwise use prop departmentId or doctor.departmentId
      const finalDepartmentId = appointment.departmentId || 
                                doctor?.departmentId || 
                                departmentId || 
                                '';
  
      // Populate form data when the modal opens
      // Ensure departmentId is set correctly - prioritize appointment.departmentId, then doctor.departmentId, then prop
      formik.setValues({
        patientId: appointment.patientId || "",
        doctorId: appointment.doctorId != null ? Number(appointment.doctorId) : "",
        appointment_date: appointmentDateTime,
        time: prefilledTime, // Set prefilled time
        isCancel: appointment.isCancel || false,
        departmentId: finalDepartmentId ? Number(finalDepartmentId) : "",
      });
    }
  }, [appointment, doctor, departmentId]);
  
  
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await getAllDepartment();
        setDepartments(data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepartments();
  }, []); 

  // Ensure prefilled time is shown once context is ready
  useEffect(() => {
    if (appointment && !formik.values.time) {
      const appointmentDateTime = appointment.appointmentDateTime
        ? dayjs(appointment.appointmentDateTime)
        : null;
      if (appointmentDateTime) {
        const prefilledTime = appointmentDateTime.format("hh:mm A");
        formik.setFieldValue("time", prefilledTime, false);
      }
    }
  }, [appointment, doctors, selectedDoctor]);
  
  const handleDepartmentChange = async (e) => {
    const departmentId = e.target.value;
    const idNum = typeof departmentId === 'string' ? Number(departmentId) : departmentId;
    formik.setFieldValue("departmentId", idNum);
    formik.setFieldValue("doctorId", "");
    formik.setFieldValue("appointment_date", null);
    formik.setFieldValue("time", "");
    setDoctors([]);
    setSelectedDoctor(null);

    try {
      // Use the new API that searches in multiple department fields
      const doctors = await getDoctorsByDepartmentIdMultiple(idNum);
      console.log("Fetched doctors with multiple departments:", doctors);
      setDoctors(doctors);
    } catch (error) {
      console.log("Error fetching doctors:", error);
      // Fallback to the old API if the new one fails
      try {
        const fallbackDoctors = await getDoctorByDepartmentId(idNum);
        console.log("Fallback doctors:", fallbackDoctors);
        setDoctors(fallbackDoctors);
      } catch (fallbackError) {
        console.log("Fallback also failed:", fallbackError);
        setDoctors([]);
      }
    }
  };
  
  const fetchDoctors = async (departmentId) => {
    if (!departmentId) return;
    try {
      const idNum = typeof departmentId === 'string' ? Number(departmentId) : departmentId;
      // Use the new API that searches in multiple department fields
      const doctors = await getDoctorsByDepartmentIdMultiple(idNum);
      console.log("Fetched doctors with multiple departments:", doctors);
      setDoctors(doctors);
    } catch (error) {
      console.log("Error fetching doctors:", error);
      // Fallback to the old API if the new one fails
      try {
        const idNum = typeof departmentId === 'string' ? Number(departmentId) : departmentId;
        const fallbackDoctors = await getDoctorByDepartmentId(idNum);
        console.log("Fallback doctors:", fallbackDoctors);
        setDoctors(fallbackDoctors);
      } catch (fallbackError) {
        console.log("Fallback also failed:", fallbackError);
        setDoctors([]);
      }
    }
  };
  
  const fetchDoctorName = async (doctorId) => {
    try {
      // Fetch doctor name based on doctorId
      const doctor = await getDoctorById(doctorId);
      if (doctor) {
        // Only set selectedDoctor if visitingSchedules is an object (not an array)
        // This ensures we have the correct structure for time slot generation
        const hasValidSchedules = doctor.visitingSchedules && 
                                  typeof doctor.visitingSchedules === 'object' && 
                                  !Array.isArray(doctor.visitingSchedules);
        
        if (hasValidSchedules) {
          setSelectedDoctor(doctor);
        } else {
          // If visitingSchedules is missing or has wrong structure, 
          // don't set selectedDoctor (it will be fetched again if needed)
          console.warn("[fetchDoctorName] Doctor fetched but visitingSchedules has invalid structure:", {
            id: doctor.id,
            visitingSchedulesType: typeof doctor.visitingSchedules,
            isArray: Array.isArray(doctor.visitingSchedules)
          });
        }
      }
    } catch (error) {
      console.error("[fetchDoctorName] Error fetching doctor:", error);
    }
  };

  const handleDoctorChange = async (e) => {
    const doctorId = e.target.value;
    const idNum = typeof doctorId === 'string' ? Number(doctorId) : doctorId;
    formik.setFieldValue("doctorId", idNum);
    formik.setFieldValue("appointment_date", null);
    formik.setFieldValue("time", "");
    const doctor = doctors.find((doc) => doc.id === idNum);
    setSelectedDoctor(doctor);
    try {
      // Fetch full doctor details (with visitingSchedules) for slots
      const full = await getDoctorById(idNum);
      if (full) setSelectedDoctor(full);
    } catch (err) {
      // ignore and keep basic doctor info
    }
  
    // Fetch existing appointments for the selected doctor
    if (formik.values.appointment_date) {
      fetchExistingAppointments(idNum, formik.values.appointment_date);
    }
  };
  
  const handleDateChange = (newDate) => {
    formik.setFieldValue("appointment_date", newDate);
    formik.setFieldValue("time", "");
  
    // Fetch existing appointments for the selected doctor and date
    if (formik.values.doctorId) {
      const idNum = typeof formik.values.doctorId === 'string' ? Number(formik.values.doctorId) : formik.values.doctorId;
      fetchExistingAppointments(idNum, newDate ?? dayjs());
    }
  };
  // When both doctorId and appointment_date are present (prefilled), load existing appointments
  // Also ensure selectedDoctor has visitingSchedules for time slots to display
  useEffect(() => {
    if (formik.values.doctorId && formik.values.appointment_date) {
      const idNum = typeof formik.values.doctorId === 'string' ? Number(formik.values.doctorId) : formik.values.doctorId;
      
      // Fetch existing appointments
      fetchExistingAppointments(idNum, formik.values.appointment_date).catch(() => {});
      
      // Ensure selectedDoctor has visitingSchedules (needed for time slots to display)
      // Only fetch if we don't have selectedDoctor or it doesn't have visitingSchedules
      if (!selectedDoctor || !selectedDoctor.visitingSchedules || selectedDoctor.id !== idNum) {
        fetchDoctorName(idNum).catch(() => {});
      }
    }
  }, [formik.values.doctorId, formik.values.appointment_date]);

  // Ensure doctors are fetched when department is preset
  useEffect(() => {
    if (formik.values.departmentId && !doctors.length) {
      fetchDoctors(formik.values.departmentId);
    }
  }, [formik.values.departmentId]);

  // Align selectedDoctor once doctors list and doctorId are available
  // Also ensure we have full doctor details with visitingSchedules
  useEffect(() => {
    if (doctors.length && formik.values.doctorId) {
      const idNum = typeof formik.values.doctorId === 'string' ? Number(formik.values.doctorId) : formik.values.doctorId;
      const doc = doctors.find((d) => d.id === idNum);
      if (doc) {
        // Only set selectedDoctor from doctors list if:
        // 1. We don't have a selectedDoctor yet, OR
        // 2. The current selectedDoctor doesn't have the correct visitingSchedules structure (object with day keys)
        const hasValidSchedules = selectedDoctor?.visitingSchedules && 
                                  typeof selectedDoctor.visitingSchedules === 'object' && 
                                  !Array.isArray(selectedDoctor.visitingSchedules);
        
        if (!selectedDoctor || !hasValidSchedules) {
          // If doctor from list doesn't have visitingSchedules or has invalid structure, fetch full details
          const docHasValidSchedules = doc.visitingSchedules && 
                                       typeof doc.visitingSchedules === 'object' && 
                                       !Array.isArray(doc.visitingSchedules);
          
          if (!docHasValidSchedules) {
            // Fetch full doctor details with proper visitingSchedules structure
            fetchDoctorName(idNum).catch(() => {});
          } else {
            // Doctor from list has valid schedules, use it
            setSelectedDoctor(doc);
          }
        }
        // If selectedDoctor already has valid schedules, don't overwrite it
      }
    } else if (formik.values.doctorId && !selectedDoctor?.visitingSchedules) {
      // If we have doctorId but no selectedDoctor with schedules, fetch it
      const idNum = typeof formik.values.doctorId === 'string' ? Number(formik.values.doctorId) : formik.values.doctorId;
      fetchDoctorName(idNum).catch(() => {});
    }
  }, [doctors, formik.values.doctorId]);

  // Helper to get slots for a given day, supporting multiple schedule shapes
  const generateTimesBetween = (startStr, endStr) => {
    try {
      if (!startStr || !endStr) return [];
      let start = dayjs(startStr, ["hh:mm A", "HH:mm"], true);
      let end = dayjs(endStr, ["hh:mm A", "HH:mm"], true);
      if (!start.isValid() || !end.isValid()) return [];
      const times = [];
      while (start.isBefore(end) || start.isSame(end)) {
        times.push(start.format("hh:mm A"));
        start = start.add(10, 'minute');
      }
      return times;
    } catch {
      return [];
    }
  };
  const getSlotsForSelectedDay = () => {
    if (!selectedDoctor || !formik.values.appointment_date) {
      return [];
    }
    
    const dayName = dayjs(formik.values.appointment_date).format("dddd");
    const dayKeyLower = dayName.toLowerCase();
    const schedules = selectedDoctor.visitingSchedules;
    
    if (!schedules) {
      return [];
    }
    
    // IMPORTANT: If schedules is an array but doesn't have the expected structure,
    // it means selectedDoctor was set incorrectly. Don't process it.
    // The correct structure is an object with day keys (e.g., { "monday": {...}, "tuesday": {...} })
    if (Array.isArray(schedules)) {
      // Check if it's actually an array of schedule objects with dayOfWeek
      const entry = schedules.find((s) => s && (s.dayOfWeek === dayName || s.dayOfWeek === dayKeyLower));
      if (entry) {
        const times = entry?.times || entry?.timings;
        if (Array.isArray(times) && times.length > 0) {
          return times;
        }
        // Fallback if only start/end provided
        if (entry?.startTime && entry?.endTime) {
          return generateTimesBetween(entry.startTime, entry.endTime);
        }
      }
      // If it's an array but doesn't match expected structure, return empty
      // This prevents incorrect processing when visitingSchedules is set incorrectly
      return [];
    }
    
    // Case 2: Object keyed by day with timings or times
    // Try multiple variations of the day key to handle different formats
    const dayVariations = [
      dayKeyLower, // "tuesday"
      dayName, // "Tuesday"
      dayKeyLower.charAt(0).toUpperCase() + dayKeyLower.slice(1), // "Tuesday" (capitalized)
      dayName.charAt(0).toLowerCase() + dayName.slice(1), // "tuesday" (lowercase)
    ];
    
    let dayObj = null;
    for (const dayKey of dayVariations) {
      if (schedules[dayKey]) {
        dayObj = schedules[dayKey];
        break;
      }
    }
    
    // Also try to find by iterating through all keys (in case of unexpected format)
    if (!dayObj && typeof schedules === 'object') {
      const allKeys = Object.keys(schedules);
      const matchingKey = allKeys.find(key => 
        key.toLowerCase() === dayKeyLower || 
        key.toLowerCase() === dayName.toLowerCase()
      );
      if (matchingKey) {
        dayObj = schedules[matchingKey];
      }
    }
    
    if (dayObj) {
      const times = dayObj?.timings || dayObj?.times;
      if (Array.isArray(times) && times.length > 0) {
        return times;
      }
      // Fallback if only start/end provided (backend often has start_time/end_time)
      if (dayObj?.start_time && dayObj?.end_time) {
        return generateTimesBetween(dayObj.start_time, dayObj.end_time);
      }
      if (dayObj?.startTime && dayObj?.endTime) {
        return generateTimesBetween(dayObj.startTime, dayObj.endTime);
      }
    }
    
    return [];
  };

   

  const handelCloseModalState = async () => {
    if (location.pathname === "/manage-patient") {
      navigate("/manage-patient");
    }
    if (location.pathname === "/path-patient") {
      navigate("/path-patient");
    }
    if (location.pathname === "/front-desk") {
      navigate("/front-desk");
    }
    if (location.pathname === "/token-dashboard" || location.pathname.includes("/token-dashboard")) {
      // Don't navigate away from token-dashboard, just close modal
      // The onSuccess callback will handle data refresh
    }
    formik.resetForm();
    setDoctors([]);
    setSelectedDoctor(null);
    onClose();
  };

  return (
    <>
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      sx={LARGE_FORM_MODAL_SX}
      title="Edit Appointment"
    >
      <Box display={"flex"} flexDirection={"column"}>
      <Box sx={DENSE_FORM_BODY_SX}>
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>

          

          <Grid item xs={6}>
              <TextField
                fullWidth
                id="departmentId"
                name="departmentId"
                label="Select Department"
                value={formik.values.departmentId}
                onChange={handleDepartmentChange}
                select
                error={
                  formik.touched.departmentId &&
                  Boolean(formik.errors.departmentId)
                }
                helperText={
                  formik.touched.departmentId && formik.errors.departmentId
                }
              >
                {filterDepartmentsForOpdWorkflow(departments, {
                  keepIds: [
                    formik.values.departmentId,
                    appointment?.departmentId,
                    departmentId,
                    doctor?.departmentId,
                  ],
                }).map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                id="doctorId"
                name="doctorId"
                label="Select Doctor"
                value={formik.values.doctorId}
                onChange={handleDoctorChange}
                select
                error={
                  formik.touched.doctorId && Boolean(formik.errors.doctorId)
                }
                helperText={formik.touched.doctorId && formik.errors.doctorId}
                disabled={!formik.values.departmentId}
              >
                {doctors.map((doc) => (
                  <MenuItem key={doc.id} value={doc.id}>
                    {doc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      label="Appointment Date"
      value={formik.values.appointment_date}
      onChange={handleDateChange}
      minDate={dayjs()}
      slotProps={{
        textField: {
          fullWidth: true,
          error:
            formik.touched.appointment_date &&
            Boolean(formik.errors.appointment_date),
          helperText:
            formik.touched.appointment_date &&
            formik.errors.appointment_date,
        },
      }}
      disabled={!formik.values.doctorId}
    />
  </LocalizationProvider>
</Grid>

<Grid item xs={6}>
  <TextField
    label="Selected Time"
    value={formik.values.time || "No time selected"} // Show prefilled time or fallback
    fullWidth
    disabled // Makes the field non-editable
    InputProps={{
      readOnly: true, // Ensures no interaction even with disabled styling
    }}
    variant="outlined" // Optional: You can customize the variant
  />
</Grid>

  <Grid item xs={12} gap={2} display={"flex"} flexWrap={"wrap"}>
  {(() => {
    // Check if visitingSchedules exists and is an object (not an array)
    const hasValidSchedules = selectedDoctor?.visitingSchedules && 
                              typeof selectedDoctor.visitingSchedules === 'object' && 
                              !Array.isArray(selectedDoctor.visitingSchedules);
    
    if (!formik.values.appointment_date || !hasValidSchedules) {
      return (
        <Typography variant="caption" color="textSecondary">
          Please select a department, doctor, and date to view available timings.
        </Typography>
      );
    }

    const timesForDay = getSlotsForSelectedDay();
    if (!timesForDay || timesForDay.length === 0) {
      return (
        <Typography variant="caption" color="textSecondary">
          No available timings for the selected date and doctor.
        </Typography>
      );
    }

    const currentTime = dayjs();
    const isToday = dayjs(formik.values.appointment_date).isSame(currentTime, 'day');

    return timesForDay.map((time, index) => {
      const slotTime = dayjs(`${dayjs(formik.values.appointment_date).format("YYYY-MM-DD")} ${time}`, "YYYY-MM-DD hh:mm A");
      const isBooked = existingAppointments.some(appointment => dayjs(appointment.appointmentDateTime).format("HH:mm") === slotTime.format("HH:mm"));
      const isDisabled = isToday && slotTime.isBefore(currentTime);
      return (
        <Box key={index}>
          <Button
            variant={formik.values.time === time ? "contained" : "outlined"}
            onClick={() => !isBooked && !isDisabled && formik.setFieldValue("time", time)}
            sx={{ minWidth: 72, mb: 1, backgroundColor: isBooked ? 'grey' : 'default', color: isBooked ? 'white' : 'default', '&:hover': { backgroundColor: isBooked ? 'darkgrey' : 'default' } }}
            disabled={isBooked || isDisabled}
          >
            {time}
          </Button>
        </Box>
      );
    });
  })()}
</Grid>

            <Grid item xs={12} mt={0} pt={0}>
              {selectedDoctor?.visitingSchedules?.[`${dayjs(formik.values.appointment_date).format("dddd").toLowerCase()}`]?.timings?.length && formik.touched.time && formik.errors.time && (
                <Typography color="error" variant="caption">
                  {formik.errors.time}
                </Typography>
              )}
            </Grid>
          </Grid>
              <FormModalActions
                onCancel={onClose}
                submitType="submit"
                submitLabel="Update Appointment"
                submitDisabled={!formik.isValid || formik.isSubmitting}
                loading={formik.isSubmitting}
              />
          </form>
        </Box>
      </Box>
      <Box>
        {/* <Button onClick={onClose} color="secondary">
          Cancel
        </Button> */}
      </Box>
      
    </BasicModal>

    <Dialog
      open={successDialogOpen}
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: "1.05rem", textAlign: "center" }}>
        Success
      </DialogTitle>
      <DialogContent sx={{ pt: "4px !important", pb: 1 }}>
        <Alert severity="success" sx={{ py: 0.5 }}>
          Appointment updated successfully.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", px: 2, pb: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          sx={FORM_SUBMIT_BUTTON_SX}
          onClick={() => {
            setSuccessDialogOpen(false);
            onClose();
            if (onSuccess && typeof onSuccess === "function") {
              onSuccess();
            } else {
              navigate("/front-desk");
            }
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditAppointmentModal;